// src/oura/visibility/model.ts
//
// The Visibility screen's one model, for both roles. Every block on the page
// (tiles, the trend, the type grid, the three tabs and the drawers) reads this
// shape and nothing else, so a count can never differ between the tile that
// states it and the table that lists its rows.
//
// Built from the live payload by fromLive.ts. A fixture business goes through
// the same builder from live-shaped data, so the preview exercises the path a
// real business takes rather than a second one that only looks like it.

import type { AeoCoverageStatus, AeoQuestionCorrected, AeoSearchMatch, AeoVerdictCorrection, AeoSourceKind } from "@service/aeo";
import { VISIBILITY_CHECK_ORDER, type VisibilityCheckName } from "../audit";
import { VISIBILITY_CHECK_LABEL } from "../lib/visibilityChecks";
import { deltaCrossesBreak } from "../trendBreak";
import { MERCHANT_V2 } from "../merchantV2";

/** The engines this screen reports, in column order. */
export type VisEngine = "chatgpt" | "gemini" | "ai_overview";
export const VIS_ENGINES: readonly VisEngine[] = ["chatgpt", "gemini", "ai_overview"];

/**
 * The engines a question's "named" verdict is made of. Google's AI is left
 * out, as the backend leaves it out of `questionsNamed`: it answers only when
 * Google chooses to render an overview, which is a fact about Google.
 */
export const ASSISTANTS: readonly VisEngine[] = ["chatgpt", "gemini"];

/**
 * The engines a scan's verdict was counted over (#4025 FE-5, backend BE-5).
 *
 * PER SCAN, from its own `countingRule`. The backend recounted
 * `questionsNamed` over every engine, but OLD SCANS KEEP THEIR STORED
 * NUMBERS until they are re-scanned -- so a screen that imposed one rule on
 * every scan would put a new headline over an old table. A question named only
 * by Google's AI would be Named in the grid and missing from the total, which
 * is the contradiction this is meant to end, arriving from the other side.
 *
 * Absent reads as "assistants": every scan stored before BE-5 was counted that
 * way, and guessing the new rule would overstate them.
 */
export function countedEngines(rule: string | null | undefined): readonly VisEngine[] {
    return rule === "all_engines" ? VIS_ENGINES : ASSISTANTS;
}

export type EngineFilter = "all" | VisEngine;

/**
 * How the All-engines number is counted, in words, wherever it is shown
 * (#4025 FE-5).
 *
 * The tester read "All engines 43, ChatGPT 36, Gemini 37, Google's AI 27" and
 * could not work out how 43 was reached -- and could not have, because the
 * rule was nowhere on the page. One constant, so the headline, the trend and
 * the type grid cannot drift apart.
 *
 * It takes the scan's own engine list, because a scan counted before the
 * backend recounted says something different and must not claim otherwise.
 */
export function namedRule(counted: readonly VisEngine[], staff = true): string {
    // The client reads the same rule about themselves.
    const who = staff ? "the business" : "you";
    return counted.length >= VIS_ENGINES.length
        ? `A question counts once if any AI engine names ${who}.`
        // The scan-age caveat is for staff; the redesign's merchant reads the
        // rule alone (flag off, the merchant keeps it).
        : `A question counts once if ChatGPT or Gemini names ${who}.${staff || !MERCHANT_V2 ? " This scan did not count Google's AI." : ""}`;
}

/** What each question-list filter means, for the ones whose name is not enough. */
export const SITE_HINT = "Your site doesn't answer: the site has a thin page, or no page, for this question.";
export function missingHint(counted: readonly VisEngine[]): string {
    return counted.length >= VIS_ENGINES.length
        ? "Missing you: no AI engine named the business on this question."
        : "Missing you: neither ChatGPT nor Gemini named the business on this question.";
}

/**
 * What "Beats it" counts, in the words the count actually uses.
 *
 * It read "by any engine" while counting two of the three; with the scan's own
 * list the sentence is true either way (#4025 FE-15), and FE-15 asks for it to
 * be visible rather than a native `title` nobody meets on touch.
 */
export function beatsHint(staff: boolean, counted: readonly VisEngine[]): string {
    const who = staff ? "this business" : "you";
    return counted.length >= VIS_ENGINES.length
        ? `counts the questions where an AI engine names them and not ${who}.`
        : `counts the questions where ChatGPT or Gemini names them and not ${who}.`;
}

/**
 * One engine's verdict on one question. `null` in its place means not measured.
 *
 * TWO STATES ONLY, and Google is not an exception (product decision on #3945,
 * 30 Sep). A question Google showed no AI Overview for, or that could not be
 * read, is **not named** — the same miss as any other, counted in the same
 * denominator of 50 as ChatGPT and Gemini.
 *
 * The first version of that ticket split those two out as states of their own,
 * on the reasoning that neither is a miss. The product's reading is that the
 * merchant is not visible on those questions either way, and that Google's
 * column has to share the other two columns' denominator to be comparable with
 * them at all. `null` is still reserved for a scan carrying no reading, which
 * is what an older scan is.
 */
export interface VisCell {
    named: boolean;
    /** Answers that named the business, of `samples` counted. One each on live. */
    hits: number;
    samples: number;
    /** The rivals this engine's counted answers named, merged names. */
    rivals: string[];
    /**
     * Google's AI only: the read itself failed (`shown: null`), so this is not
     * a miss and the type grid leaves it out of both sides of "n of m" (#4053).
     * Absent everywhere else, and `named` stays false so no other screen moves.
     */
    unread?: boolean;
}

/** What an engine said, as the scan stored it on the probe (trimmed by the backend). */
export interface VisExcerpt {
    text: string;
    truncated: boolean;
    /** The probe's own instrument, e.g. `chatgpt_app`. */
    instrument: string | null;
    citedPages: string[];
}

export interface VisRow {
    /** The question's slot `n`, or its position when the set carries none. */
    id: number;
    /** Trimmed lowercase text: the join key the backend also uses. */
    key: string;
    text: string;
    kind: VisibilityCheckName | null;
    /** Real Google searches that landed on this slot, strongest first. */
    searches: AeoSearchMatch[];
    cells: Record<VisEngine, VisCell | null>;
    /** At least one assistant named the business. */
    named: boolean;
    /** At least one assistant gave a countable answer. */
    measured: boolean;
    site: AeoCoverageStatus | null;
    /**
     * The rivals the assistants' counted answers named on this question,
     * merged names: the rows a rival's Competitors count is made of. Google's
     * AI rivals live on its own cell.
     */
    rivals: string[];
    excerpts: Partial<Record<VisEngine, VisExcerpt>>;
    /** The plan task that covers this question, when the plan names one. */
    fixTaskId: string | null;
    /** That task's own action, so a Fix link can say what it opens. */
    fixTaskTitle: string | null;
    /** Google's AI page for this question is still being read: its cell is
     *  `null` (not counted), and it is shown as pending, never as a miss. */
    googlePending: boolean;
    /** Staff corrected the wording and asked it again (Edited · V2). Staff-only on screen. Absent on samples. */
    corrected?: AeoQuestionCorrected | null;
    /** A correction is clearing the old answers or asking again: the answers are empty until it lands. */
    reasking?: boolean;
    /** Staff corrected an engine's verdict on this question (who, when, why). Staff-only. Absent on samples. */
    verdictFixes?: Partial<Record<VisEngine, AeoVerdictCorrection>>;
}

/**
 * Google's AI pages still being read on this scan (ourteam-backend#5619).
 * `null` on the model when nothing is pending, so every screen renders as it
 * did before. `pages`/`of` are null when the read has only a sentence to say
 * (nothing read yet).
 */
export interface VisGooglePending {
    pages: number | null;
    of: number | null;
    /** `true` running, `false` stopped (staff refresh), `null` unknown. */
    rereading: boolean | null;
    /** The backend's own sentence, for the case with no counts. */
    sentence: string | null;
}

export interface VisEngineStat {
    key: VisEngine;
    /** The name a reader sees. The ChatGPT column reads "OpenAI" on an API run. */
    label: string;
    named: number | null;
    of: number | null;
    /** How the figure was taken, in the run's own words. Staff only. */
    instrument: string | null;
    /** Why there is no figure, when there is none. */
    skipped: string | null;
    /** When this engine's figure was read, where that differs from the scan. */
    capturedAt: string | null;
    /** Google's AI only: what was asked and what Google showed. */
    coverage: { asked: number; answered: number; notShown: number; failed: number; pending?: number } | null;
    /** This engine has a verdict per question, so its column and filter work. */
    perQuestion: boolean;
}

export interface VisTrendPoint {
    date: string | null;
    values: Record<EngineFilter, number | null>;
    /** What each value is out of, on that scan. Null where the history does not say. */
    of: Record<EngineFilter, number | null>;
}

/** Why the line stops being one measurement at `breakIndex`: the questions,
 *  how ChatGPT was asked, or another change of method (a model). */
export type VisTrendBreakCause = "questions" | "instrument" | "method";

export interface VisTrend {
    /**
     * What a point counts. `questions` is the named-count history; `answers`
     * is the live fallback, mentions summed across engines, which cannot be
     * relabelled as questions without making a false number.
     */
    unit: "questions" | "answers";
    points: VisTrendPoint[];
    /** First point measured the current way, or null (trendBreak.ts). */
    breakIndex: number | null;
    breakReason: string | null;
    /** A changed question set, ChatGPT asked a different way (API against app), or a changed model. */
    breakCause: VisTrendBreakCause | null;
}

export interface VisHeadline {
    named: number;
    of: number;
    asked: number;
    /** The backend's verdict word, keyed off the weakest engine. */
    verdict: string | null;
    weakest: VisEngineStat | null;
    /** A typical business we measure. Absent on live until the benchmark ships. */
    benchmark: number | null;
    /** Why there is no figure, in words, when `of` is 0. */
    absent: string | null;
}

export interface VisRival {
    name: string;
    domain: string | null;
    /** Questions it is named on, named or not. */
    on: number;
    /** Questions it is named on that the business is not. */
    wins: VisRow[];
    /**
     * How many questions it takes from the business: the backend's own
     * `insteadCount` when the scan sends it, else `wins.length`. Every
     * "takes N" and "Beats" count reads this one number.
     */
    takes: number;
    /** Questions both are named on. */
    both: VisRow[];
    /** What it has that the business does not, from a verified pass. */
    gap: string | null;
    /** The report's own paragraph on this rival (`report_copy.competitors`). */
    narrative: string | null;
    /** The scan's own line on a rival it found, when that says more than the count. */
    note: string | null;
    /** False when the scan could not attach a count to this rival. */
    scored: boolean;
}

export interface VisSourceRow {
    domain: string;
    count: number;
    own: boolean;
    kind: AeoSourceKind | null;
    /** The slots behind `count`, when the payload lists them. */
    questions: number[] | null;
}

export interface VisSources {
    /** The measured own-site share, or null when there is none to state. */
    own: { count: number; of: number; basis: string | null; engines: string[] } | null;
    /** An older scan's count, with no denominator to divide by. */
    legacyOwn: number | null;
    absent: string | null;
    /** Rented visibility: the mentions come from other people's sites. */
    capped: boolean;
    rows: VisSourceRow[];
    /** Distinct sites in all, beyond the rows listed. */
    total: number | null;
    /** What a row's count counts (`questions`, or answers on older scans). */
    basis: string | null;
    /** The logged-out ChatGPT app shows no source links on this scan. */
    appShowsNoLinks: boolean;
    /** The engines the source rows were read from. */
    from: string[];
}

export interface VisGoogle {
    connected: boolean;
    avgPosition: number | null;
    top10: number | null;
    clicks: number | null;
    days: number | null;
    keywords: { query: string; clicks: number; position: number }[];
}

export interface VisOpsRow {
    label: string;
    value: string;
    engine?: VisEngine;
    tone?: "warn";
}

export interface VisModel {
    capturedAt: string;
    /**
     * The engines THIS scan's "named" verdict counts over, from its own
     * `countingRule`. Read it rather than a constant: the rule is per scan
     * (`countedEngines`).
     */
    counted: readonly VisEngine[];
    /** A stored picture of the ChatGPT answer by slot. Fixture only; live
     *  captures are fetched per question from the answer store. */
    captureFor: ((slot: number) => { src: string; takenAt: string | null } | null) | null;
    headline: VisHeadline;
    engines: Record<VisEngine, VisEngineStat>;
    /** Google's AI pages still being read, or null when none are (ourteam-backend#5619). */
    googlePending: VisGooglePending | null;
    rows: VisRow[];
    trend: VisTrend;
    /**
     * Why a question has no search chip, for the whole scan (topSearches.ts).
     * `wholeScan`: no question has a search, so the notice above the table
     * says it once and the rows carry no empty chip.
     */
    searchEmpty: { label: string; why: string; notBuilt: boolean; wholeScan: boolean };
    /** The check's own question and fix, from the backend, per type. */
    kindCopy: Partial<Record<VisibilityCheckName, { question: string; fix: string }>>;
    rivals: VisRival[];
    /**
     * Every other business an engine named, beyond the rivals listed, with no
     * count behind it: the long tail the backend keeps per engine.
     */
    otherRivals: string[];
    sources: VisSources;
    google: VisGoogle;
    ops: VisOpsRow[];
    /** The staff warning line, when the instrument changes what the figures mean. */
    instrumentWarning: string | null;
    /** The ChatGPT column's full instrument label and note, for its header. */
    chatgptInstrument: string | null;
    hasSite: boolean;
    /** Some cell counts more than one answer, so "2/3" needs its key. */
    hasSamples: boolean;
}

/** What the site's closest page does for a question, in words. */
export const SITE_LABEL: Record<AeoCoverageStatus, string> = {
    covered: "Answers it",
    partial: "Thin",
    missing: "No page",
};

/* ------------------------------------------------------------------------- *
 * Rivals, per question and per engine
 * ------------------------------------------------------------------------- */

const nameKey = (name: string) => name.trim().toLowerCase();

/** Every rival any engine named on the question, merged names, first seen first. */
export function allRivals(row: VisRow): string[] {
    const seen = new Map<string, string>();
    [...row.rivals, ...VIS_ENGINES.flatMap((e) => row.cells[e]?.rivals ?? [])].forEach((n) => {
        if (n.trim() && !seen.has(nameKey(n))) seen.set(nameKey(n), n);
    });
    return Array.from(seen.values());
}

/**
 * Who was named in the business's place on one question: the rivals in the
 * answers of the engines that did not name it, only the filtered engine's
 * when one is chosen. `lead` (the "Lost to" rival) goes first when present.
 */
export function namedInstead(row: VisRow, engine: EngineFilter, lead: string | null = null): string[] {
    const engines = engine === "all" ? VIS_ENGINES : [engine];
    const seen = new Map<string, string>();
    engines.forEach((e) => {
        const cell = row.cells[e];
        if (!cell || cell.named) return;
        cell.rivals.forEach((n) => {
            if (n.trim() && !seen.has(nameKey(n))) seen.set(nameKey(n), n);
        });
    });
    const names = Array.from(seen.values());
    const at = lead ? names.findIndex((n) => nameKey(n) === nameKey(lead)) : -1;
    return at > 0 ? [names[at], ...names.slice(0, at), ...names.slice(at + 1)] : names;
}

/** Each rival on a question with the engines that named it, split by whether that engine also named the business. */
export interface RivalMention {
    name: string;
    /** Engines that named this rival and not the business. */
    instead: VisEngine[];
    /** Engines that named this rival beside the business. */
    beside: VisEngine[];
}

export function rivalMentions(row: VisRow): RivalMention[] {
    const by = new Map<string, RivalMention>();
    VIS_ENGINES.forEach((e) => {
        const cell = row.cells[e];
        if (!cell) return;
        cell.rivals.forEach((n) => {
            if (!n.trim()) return;
            const k = nameKey(n);
            const m = by.get(k) ?? { name: n, instead: [], beside: [] };
            (cell.named ? m.beside : m.instead).push(e);
            by.set(k, m);
        });
    });
    return Array.from(by.values()).sort((a, b) => b.instead.length - a.instead.length || b.beside.length - a.beside.length);
}

/**
 * The Competitors list as one engine saw it: the same rivals, counted from
 * that engine's own cells, so "Beats it" under Gemini is Gemini's answers
 * only. A rival that engine never named drops out. `all` is the model's own
 * list, counted over the assistants.
 */
export function rivalsOn(model: VisModel, engine: EngineFilter): VisRival[] {
    if (engine === "all") return model.rivals;
    return model.rivals
        .map((r) => {
            const on = model.rows.filter((row) => row.cells[engine]?.rivals.some((n) => nameKey(n) === nameKey(r.name)));
            const wins = on.filter((row) => !row.cells[engine]?.named);
            return {
                ...r,
                on: on.length,
                wins,
                // The backend counts over every engine; one engine's own count is its cells.
                takes: wins.length,
                both: on.filter((row) => row.cells[engine]?.named),
            };
        })
        .filter((r) => r.on > 0)
        .sort((a, b) => b.takes - a.takes || b.on - a.on);
}

/** The business's own line in the Competitors list: the headline, or one engine's figure. */
export function namedOn(model: VisModel, engine: EngineFilter): { named: number; of: number } | null {
    if (engine === "all") return model.headline.of > 0 ? { named: model.headline.named, of: model.headline.of } : null;
    const s = model.engines[engine];
    return s.named !== null && s.of !== null && s.of > 0 ? { named: s.named, of: s.of } : null;
}

/* ------------------------------------------------------------------------- *
 * Question filters
 * ------------------------------------------------------------------------- */

export type QuestionFilter = string;

export interface FilterDef {
    key: QuestionFilter;
    /**
     * What this filter means, shown under the chips while it is the active
     * one. A visible explanation, because a native `title` is invisible on
     * touch and on a keyboard -- which is the whole reason these two chips
     * were unexplained (#4025 FE-10).
     */
    hint?: string;
    label: string;
    count: number;
    test: (row: VisRow) => boolean;
}

const LOST = "lost:";
const KIND = "kind:";

export function lostFilter(rival: string): QuestionFilter {
    return `${LOST}${rival}`;
}

/** The rival a "Lost to" filter names, or null for any other filter. */
export function lostRival(filter: QuestionFilter): string | null {
    return filter.startsWith(LOST) ? filter.slice(LOST.length) : null;
}

export function kindFilter(kind: VisibilityCheckName): QuestionFilter {
    return `${KIND}${kind}`;
}

/**
 * Which engine's tab a question's drawer opens on (#4025 FE-8).
 *
 * The engine the page is filtered to, because a reader who narrowed the table
 * to Gemini and clicked a row is asking about Gemini. Under All engines, the
 * first engine that actually named the business: a reader opening a row they
 * can see is Named wants the answer that named them, and landing on an engine
 * that did not would read as a contradiction. Failing both, the first tab, so
 * there is always an answer on screen.
 */
export function openEngineTab(row: VisRow, pageEngine: EngineFilter): VisEngine {
    if (pageEngine !== "all") return pageEngine;
    return VIS_ENGINES.find((e) => row.cells[e]?.named) ?? VIS_ENGINES[0];
}

export function isKindFilter(filter: QuestionFilter): boolean {
    return filter.startsWith(KIND);
}

function missingOn(engine: EngineFilter): (row: VisRow) => boolean {
    if (engine === "all") return (row) => row.measured && !row.named;
    return (row) => row.cells[engine] !== null && !row.cells[engine]?.named;
}

/**
 * The rows a type-grid cell counts, and so the rows its click lists: the
 * type's questions the engine measured (any assistant, under All). A row the
 * engine did not measure is in neither, so "1 of 8" opens 8 rows.
 */
function kindTest(kind: VisibilityCheckName, engine: EngineFilter): (row: VisRow) => boolean {
    if (engine === "all") return (row) => row.kind === kind && row.measured;
    // A Google read that failed is not a question Google answered (#4053).
    return (row) => row.kind === kind && row.cells[engine] !== null && row.cells[engine]?.unread !== true;
}

/**
 * The filter chips, with the rows behind each count.
 *
 * "Searched on Google", not "Top searches": a row whose only search ranks
 * 126th is in it, and its own chip says so. "Lost to" and the type filter
 * appear only while active, because they are reached from the Competitors
 * tab and the type grid rather than chosen here. Both follow the engine
 * filter, so they list what the tab and the grid counted under it.
 */
export function questionFilters(model: VisModel, engine: EngineFilter, active: QuestionFilter): FilterDef[] {
    const onEngine = engine === "all" ? "" : ` on ${model.engines[engine].label}`;
    const defs: Omit<FilterDef, "count">[] = [
        { key: "all", label: "All", test: () => true },
        {
            key: "missing",
            label: engine === "all" ? "Missing you" : `Missing${onEngine}`,
            hint: engine === "all"
                ? missingHint(model.counted)
                : `Missing on ${model.engines[engine].label}: that engine did not name the business on this question.`,
            test: missingOn(engine),
        },
    ];
    if (model.hasSite) {
        // A different thing from "Missing you", and they sounded like the same
        // thing: one is about the engines, the other about the website.
        defs.push({ key: "site", label: "Your site doesn't answer", hint: SITE_HINT, test: (r) => r.site !== null && r.site !== "covered" });
    }
    if (model.rows.some((r) => r.searches.length > 0)) {
        defs.push({ key: "searched", label: "Searched on Google", test: (r) => r.searches.length > 0 });
    }
    const rival = lostRival(active);
    if (rival !== null) {
        // The same rows the Competitors tab counts as that rival's wins, so
        // "See all 8 in the table" lands on 8. A name no longer in the list
        // falls back to the rows where it was named instead.
        const listed = rivalsOn(model, engine).find((r) => r.name === rival);
        defs.push({
            key: active,
            label: `Lost to ${rival}${onEngine}`,
            test: listed
                ? (r) => listed.wins.includes(r)
                : (r) => namedInstead(r, engine).some((n) => nameKey(n) === nameKey(rival)),
        });
    }
    // One chip per question type the set measured (#4025 FE-11). These existed
    // as filters already, and were reachable ONLY by clicking a cell in Named
    // by question type -- so a reader who wanted "just the Location questions"
    // had to know to use the grid as a control. They are offered here in the
    // spec's own order, and the active one is not added twice.
    for (const kind of kindsOf(model.rows, VISIBILITY_CHECK_ORDER)) {
        const key = kindFilter(kind);
        if (defs.some((d) => d.key === key)) continue;
        const name = VISIBILITY_CHECK_LABEL[kind];
        if (name) defs.push({ key, label: `${name}${onEngine}`, test: kindTest(kind, engine) });
    }
    if (isKindFilter(active) && !defs.some((d) => d.key === active)) {
        // A type the current rows no longer carry, still named in the URL: it
        // keeps its chip so the page it describes is not silently relabelled.
        const kind = active.slice(KIND.length) as VisibilityCheckName;
        const name = VISIBILITY_CHECK_LABEL[kind];
        if (name) defs.push({ key: active, label: `${name} questions${onEngine}`, test: kindTest(kind, engine) });
    }
    return defs.map((d) => ({ ...d, count: model.rows.filter(d.test).length }));
}

/* ------------------------------------------------------------------------- *
 * The type grid
 * ------------------------------------------------------------------------- */

export interface GridCell {
    named: number;
    of: number;
    /** Questions of this type whose read failed: in neither `named` nor `of`. */
    unread: number;
}

/**
 * Named per type, per engine, counted from the same rows the table lists, so
 * a cell's "4 of 12" is exactly the rows its click filters to. `null` is an
 * engine with no per-question verdict for that type on this scan.
 */
export function gridCell(rows: VisRow[], kind: VisibilityCheckName, engine: EngineFilter): GridCell | null {
    const counted = rows.filter(kindTest(kind, engine));
    const unread = engine === "all" ? 0 : rows.filter((r) => r.kind === kind && r.cells[engine]?.unread === true).length;
    if (counted.length === 0 && unread === 0) return null;
    const named = engine === "all" ? counted.filter((r) => r.named) : counted.filter((r) => r.cells[engine]?.named);
    return { named: named.length, of: counted.length, unread };
}

/** The types this set measured, in the spec's order. */
export function kindsOf(rows: VisRow[], order: readonly VisibilityCheckName[]): VisibilityCheckName[] {
    return order.filter((k) => rows.some((r) => r.kind === k));
}

/* ------------------------------------------------------------------------- *
 * The trend
 * ------------------------------------------------------------------------- */

export interface TrendDelta {
    value: number;
    /** The earlier point's date. */
    since: string | null;
    /** False across a change of method (questions or instrument): the caller then shows nothing. */
    comparable: boolean;
}

/**
 * The move between the last two points of one series, through the one break
 * rule every delta in the product follows (trendBreak.ts). Null when either
 * point is missing: a gap is not a zero.
 */
export function trendDelta(trend: VisTrend, engine: EngineFilter): TrendDelta | null {
    if (trend.unit !== "questions") return null;
    const values = trend.points.map((p) => p.values[engine]);
    const last = values.length - 1;
    if (last < 1) return null;
    const now = values[last];
    const before = values[last - 1];
    if (now === null || before === null) return null;
    const series = values.map((v) => v ?? 0);
    return {
        value: now - before,
        since: trend.points[last - 1].date,
        comparable: !deltaCrossesBreak(series, trend.breakIndex),
    };
}

/**
 * The weakest of the engines measured on this scan, by share named, or null
 * with fewer than two. An engine not read (Google's AI when Google showed no
 * overview, or the read failed) carries `skipped` and is never the weakest.
 *
 * NULL ON A TIE, which is the common case staff kept seeing: a client named
 * by nothing is at 0 on all three, and "ChatGPT is the weak engine" then reads
 * as a finding about ChatGPT rather than about the client (#3989). Weakest
 * means one engine is worse than the others; when none is, there is nothing
 * to name.
 */
export function weakestEngine(stats: VisEngineStat[]): VisEngineStat | null {
    const rated = stats.filter((e) => e.skipped === null && e.named !== null && e.of !== null && e.of > 0);
    if (rated.length < 2) return null;
    const rate = (e: VisEngineStat) => (e.named ?? 0) / (e.of ?? 1);
    const lowest = rated.reduce((a, b) => (rate(b) < rate(a) ? b : a));
    return rated.filter((e) => rate(e) === rate(lowest)).length > 1 ? null : lowest;
}

/** 0..100, rounded, or null when there is nothing to divide. */
export function pct(named: number | null, of: number | null): number | null {
    if (named === null || of === null || of <= 0) return null;
    return Math.round((named / of) * 100);
}
